import { useEffect, useState } from "react";
import { TrendingUp, Wallet, ShoppingCart, ShieldCheck, Landmark, BarChart3, PiggyBank, Percent, Target } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { deltaVs, pctFmt } from "./categories";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const VIOLET = "#6D5DF6";
const KPI_DEFS = [
  ["encaisse", "CA encaissé", TrendingUp, "#E7F6EC", "#16A34A"],
  ["dep_pro", "Dépenses professionnelles", Wallet, "#F1EFFE", VIOLET],
  ["dep_perso", "Dépenses personnelles", ShoppingCart, "#FFF3E8", "#EA580C"],
  ["urssaf", "URSSAF estimée", ShieldCheck, "#FDEAF3", "#DB2777"],
  ["impots_provisions", "Impôts et provisions", Landmark, "#E8F0FE", "#1D4ED8"],
  ["resultat_pro", "Résultat pro (après charges)", BarChart3, "#E8FAFA", "#0D9488"],
  ["reste_a_vivre", "Reste à vivre réel", PiggyBank, "#F1EFFE", VIOLET],
  ["taux_rentabilite", "Taux de rentabilité", Percent, "#FEF9E7", "#B45309"],
];
const CHART_COLORS = { encaisse: VIOLET, dep_pro: "#DB2777", dep_perso: "#F59E0B", urssaf: "#93C5FD", reste_a_vivre: "#16A34A" };
const DONUT = [VIOLET, "#22C55E", "#F59E0B", "#DB2777", "#0D9488", "#94A3B8", "#F97316", "#64748B"];
const frMonth = (key) => new Date(`${key}-15`).toLocaleDateString("fr-FR", { month: "short" });

function Delta({ cur, prev, suffix = " €" }) {
  const d = deltaVs(cur, prev);
  if (d.pct === null) return <span className="text-[11px] text-neutral-400">—</span>;
  const up = d.pct >= 0;
  return (
    <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${up ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-500"}`}>
      {pctFmt(d.pct)}{suffix === "%" ? "" : ""}
    </span>
  );
}

function KpiCard({ k, data, prev }) {
  const [, label, Icon, bg, fg] = KPI_DEFS.find((x) => x[0] === k);
  const isPct = k === "taux_rentabilite";
  const val = isPct ? `${data.taux_rentabilite} %` : eur(data[k]);
  return (
    <div className="bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`pilotage-kpi-${k}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: bg, color: fg }}><Icon className="w-4 h-4" /></span>
        <Delta cur={data[k]} prev={prev?.[k] ?? 0} />
      </div>
      <p className="text-[11px] text-neutral-400 font-medium">{label}</p>
      <p className="text-xl font-bold text-[#0B132B] mt-0.5">{val}</p>
      {k === "urssaf" && <p className="text-[10px] text-neutral-400 mt-0.5">{data.taux_urssaf} % du CA encaissé</p>}
      {!isPct && prev && <p className="text-[10px] text-neutral-400 mt-0.5">vs mois préc. ({eur(prev[k] || 0)})</p>}
    </div>
  );
}

function Donut({ title, rows, totalLabel }) {
  const total = rows.reduce((s, r) => s + r.amount, 0);
  return (
    <div className="bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`pilotage-donut-${totalLabel}`}>
      <h3 className="text-sm font-semibold text-[#0B132B] mb-1">{title}</h3>
      {total === 0 ? <p className="text-xs text-neutral-400 py-8 text-center">Aucune dépense sur la période.</p> : (
        <div className="flex items-center gap-4">
          <div className="w-36 h-36 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart><Pie data={rows} dataKey="amount" nameKey="label" innerRadius={42} outerRadius={64} strokeWidth={0}>
                {rows.map((_, i) => <Cell key={i} fill={DONUT[i % DONUT.length]} />)}
              </Pie><RTooltip formatter={(v) => eur(v)} /></PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="flex-1 space-y-1 min-w-0">
            {rows.slice(0, 7).map((r, i) => (
              <li key={r.category} className="flex items-center gap-2 text-[11px]">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: DONUT[i % DONUT.length] }} />
                <span className="truncate text-neutral-600 flex-1">{r.label}</span>
                <span className="font-medium text-neutral-800">{eur(r.amount)}</span>
                <span className="text-neutral-400 w-8 text-right">{r.pct} %</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ObjectifsCard({ objectifs, kpis, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(objectifs);
  useEffect(() => setF(objectifs), [objectifs]);
  const rows = [
    ["Chiffre d'affaires", kpis.encaisse, objectifs.ca_mensuel, objectifs.ca_progress_pct, VIOLET],
    ["Reste à vivre", kpis.reste_a_vivre, objectifs.reste_a_vivre, objectifs.reste_progress_pct, "#16A34A"],
    ["Budget dépenses pro", kpis.dep_pro, objectifs.budget_pro, objectifs.budget_pro_pct, "#DB2777"],
    ["Budget dépenses perso", kpis.dep_perso, objectifs.budget_perso, objectifs.budget_perso_pct, "#F59E0B"],
  ];
  const save = async () => {
    try {
      await api.put("/pilotage/settings", { objectifs: { ca_mensuel: +f.ca_mensuel || 0, reste_a_vivre: +f.reste_a_vivre || 0, budget_pro: +f.budget_pro || 0, budget_perso: +f.budget_perso || 0 } });
      toast.success("Objectifs enregistrés"); setEdit(false); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="pilotage-objectifs">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[#0B132B] flex items-center gap-1.5"><Target className="w-4 h-4" style={{ color: VIOLET }} />Objectifs du mois</h3>
        <button onClick={() => setEdit(!edit)} className="text-[11px] font-medium hover:underline" style={{ color: VIOLET }} data-testid="pilotage-objectifs-edit">{edit ? "Annuler" : "Modifier"}</button>
      </div>
      {edit ? (
        <div className="space-y-2">
          {[["ca_mensuel", "Objectif CA mensuel"], ["reste_a_vivre", "Reste à vivre cible"], ["budget_pro", "Budget dépenses pro"], ["budget_perso", "Budget dépenses perso"]].map(([k, l]) => (
            <label key={k} className="flex items-center justify-between gap-2 text-xs text-neutral-500">{l}
              <Input type="number" min="0" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="w-28 h-8 text-xs" data-testid={`obj-${k}`} /></label>
          ))}
          <Button onClick={save} className="w-full h-8 text-xs text-white" style={{ background: VIOLET }} data-testid="obj-save">Enregistrer</Button>
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map(([l, cur, target, pct, color]) => (
            <li key={l}>
              <div className="flex justify-between text-[11px] mb-1">
                <span className="text-neutral-500">{l}</span>
                <span className="font-medium text-neutral-700">{eur(cur)}{target ? ` / ${eur(target)}` : ""}</span>
              </div>
              <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct ?? 0)}%`, background: color }} />
              </div>
              {target ? <p className={`text-[10px] mt-0.5 text-right font-medium ${pct > 100 && l.startsWith("Budget") ? "text-rose-500" : "text-neutral-400"}`}>{pct} %</p> : <p className="text-[10px] mt-0.5 text-right text-neutral-300">objectif non défini</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function PilotageOverview({ from, to }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/pilotage/overview", { params: { frm: from, to } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { setData(null); load(); }, [from, to]); // eslint-disable-line
  if (!data) return <div className="py-16 text-center text-sm text-neutral-400">Chargement du pilotage…</div>;
  const { kpis, prev_month } = data;
  return (
    <div className="space-y-4" data-testid="pilotage-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {KPI_DEFS.map(([k]) => <KpiCard key={k} k={k} data={kpis} prev={prev_month} />)}
      </div>
      <div className="grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="pilotage-evolution">
          <h3 className="text-sm font-semibold text-[#0B132B] mb-2">Évolution financière sur 12 mois</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.evolution} margin={{ left: -14, right: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="key" tickFormatter={frMonth} tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" tickFormatter={(v) => `${Math.round(v / 100) / 10}k`} />
                <RTooltip formatter={(v, n) => [eur(v), { encaisse: "CA encaissé", dep_pro: "Dépenses pro", dep_perso: "Dépenses perso", urssaf: "URSSAF", reste_a_vivre: "Reste à vivre" }[n] || n]} labelFormatter={(k) => new Date(`${k}-15`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })} />
                <Bar dataKey="encaisse" fill={CHART_COLORS.encaisse} radius={[3, 3, 0, 0]} maxBarSize={14} />
                <Bar dataKey="dep_pro" fill={CHART_COLORS.dep_pro} radius={[3, 3, 0, 0]} maxBarSize={14} />
                <Bar dataKey="dep_perso" fill={CHART_COLORS.dep_perso} radius={[3, 3, 0, 0]} maxBarSize={14} />
                <Bar dataKey="urssaf" fill={CHART_COLORS.urssaf} radius={[3, 3, 0, 0]} maxBarSize={14} />
                <Line dataKey="reste_a_vivre" stroke={CHART_COLORS.reste_a_vivre} strokeWidth={2} dot={{ r: 2.5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <Donut title="Répartition des dépenses pro" rows={data.repartition_pro} totalLabel="pro" />
      </div>
      <div className="grid lg:grid-cols-3 gap-3">
        <Donut title="Répartition des dépenses personnelles" rows={data.repartition_perso} totalLabel="perso" />
        <ObjectifsCard objectifs={data.objectifs} kpis={kpis} onSaved={load} />
        <div className="bg-white border border-neutral-100 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="pilotage-reste-detail">
          <h3 className="text-sm font-semibold text-[#0B132B] mb-3">Reste à vivre — le détail</h3>
          <ul className="space-y-1.5 text-xs">
            <li className="flex justify-between"><span className="text-neutral-500">Encaissements réels</span><span className="font-medium text-emerald-600">+ {eur(kpis.encaisse)}</span></li>
            <li className="flex justify-between"><span className="text-neutral-500">Dépenses pro payées</span><span className="font-medium text-rose-500">− {eur(kpis.dep_pro)}</span></li>
            <li className="flex justify-between"><span className="text-neutral-500">URSSAF estimée ({kpis.taux_urssaf} %)</span><span className="font-medium text-rose-500">− {eur(kpis.urssaf)}</span></li>
            <li className="flex justify-between"><span className="text-neutral-500">Impôts provisionnés</span><span className="font-medium text-rose-500">− {eur(kpis.impots_provisions)}</span></li>
            <li className="flex justify-between"><span className="text-neutral-500">Dépenses personnelles</span><span className="font-medium text-rose-500">− {eur(kpis.dep_perso)}</span></li>
            <li className="flex justify-between border-t border-neutral-100 pt-1.5 mt-1"><span className="font-semibold text-[#0B132B]">Reste à vivre</span><span className="font-bold" style={{ color: kpis.reste_a_vivre >= 0 ? "#16A34A" : "#E11D48" }}>{eur(kpis.reste_a_vivre)}</span></li>
          </ul>
          {data.ca_minimum_reste_cible && (
            <div className="mt-3 rounded-xl p-3 text-[11px]" style={{ background: "#F1EFFE" }} data-testid="pilotage-ca-minimum">
              <p className="font-semibold text-[#0B132B]">Objectif reste à vivre : CA minimum à encaisser</p>
              <p className="text-lg font-bold mt-0.5" style={{ color: VIOLET }}>{eur(data.ca_minimum_reste_cible)}<span className="text-[10px] font-normal text-neutral-400"> / mois</span></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
